<script setup lang="ts">
/**
 * Confirm dialog: delete, cancel a run, revoke a key, leave — and the safe confirms (sync again,
 * archive). One sentence of consequence and the facts that make it irreversible; never a typed
 * confirmation. Footer as in every dialog (founder 2026-09-10): the way out, then the one action,
 * together at the right. danger = the outlined red action; primary = the gradient of a safe one.
 */
import Dialog from '~/components/ui/Dialog.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import ParamRow from '~/components/inspector/ParamRow.vue'
import type { IconName } from '~/utils/icons'

const props = withDefaults(defineProps<{
  title: string
  entity: string
  consequence: string
  facts?: { label: string; value: string }[]
  confirmLabel: string
  tone?: 'danger' | 'primary'
  icon?: IconName
  cancelLabel?: string
}>(), { facts: () => [], tone: 'danger', icon: undefined, cancelLabel: undefined })
defineEmits<{ close: []; confirm: [] }>()
const glyph = computed<IconName>(() => props.icon ?? (props.tone === 'danger' ? 'trash' : 'check'))
/* One way out, worded the same in every confirmation. It used to read "Keep it" on a
   destructive dialog and "Cancel" on a safe one, which put two words for one control on
   neighbouring screens — and read as a second "Cancel" beside "Cancel run" (W8-UI-07). */
const back = computed(() => props.cancelLabel ?? 'Go back')
</script>

<template>
  <Dialog :title="title" :meta="entity" :width="460" @close="$emit('close')">
    <template #icon><Icon :name="glyph" /></template>

    <p class="m-0 text-xs leading-relaxed text-pretty">{{ consequence }}</p>

    <div v-if="facts.length" class="flex flex-col gap-[var(--measure-7px)] rounded-md border border-border p-2.5">
      <ParamRow v-for="f in facts" :key="f.label" :label="f.label" :value="f.value" />
    </div>

    <template #actions>
      <ActionButton :label="back" @click="$emit('close')" />
      <ActionButton :label="confirmLabel" :variant="tone === 'danger' ? 'danger' : 'primary'" @click="$emit('confirm')"><template #icon><Icon :name="glyph" /></template></ActionButton>
    </template>
  </Dialog>
</template>
